// @flow
import React, {Component} from 'react';
import {Animated, TextInput, View, Platform} from 'react-native';
import {Text} from 'trpc-react-native-ui';
import type {InputProps, MetaProps} from 'redux-form';
import theme from '../../theme-config';
import StyleSheet from '../../lib/rtl-aware-stylesheet';

const isIos = Platform.OS === 'ios';

export const USE_NATIVE_DRIVER = Platform.select({
  ios: true,
  android: Platform.constants?.Release !== '12',
  default: true,
});

export default class Hoshi extends Component<HoshiInputPropsType, State> {
  state: State;
  height: number;
  styles: any;
  inputRef: any;

  constructor(props: HoshiInputPropsType) {
    super(props);

    this.state = {
      width: null,
      focusedAnim: new Animated.Value(
        props.meta.active || props.input.value ? 1 : 0,
      ),
    };
    this.styles = getStyles();
  }

  getTypeProps() {
    const {type = 'text'} = this.props;
    switch (type) {
      case 'email':
        return {
          autoCorrect: false,
          keyboardType: 'email-address',
          autoCapitalize: 'none',
        };
      case 'password':
        return {
          autoCorrect: false,
          secureTextEntry: true,
          autoCapitalize: 'none',
          textContentType: 'oneTimeCode', // https://github.com/facebook/react-native/issues/21572
        };
      case 'digits': {
        return {
          keyboardType: 'numeric',
        };
      }
      default:
        return {};
    }
  }

  UNSAFE_componentWillReceiveProps(props: HoshiInputPropsType) {
    if (
      this.props.meta.active === props.meta.active &&
      !!this.props.input.value === !!props.input.value
    ) {
      return;
    }
    Animated.timing(this.state.focusedAnim, {
      toValue: props.input.value || props.meta.active ? 1 : 0,
      duration: props.animationDuration,
      easing: props.easing,
      useNativeDriver: USE_NATIVE_DRIVER,
    }).start();
  }

  _onLayout = event => {
    this.setState({
      width: event.nativeEvent.layout.width,
    });
  };

  setInputRef = (inputRef: any) => {
    this.inputRef = inputRef;
  };

  render() {
    const styles = this.styles;
    const {
      style: containerStyle,
      wrapperContainerStyle,
      meta,
      icon,
      inputStyle,
      input,
      inversedColors,
      disabled,
      validateWhileTyping,
      errorNumberOfLines = 1,
      ...otherProps
    } = this.props;

    const {width, focusedAnim} = this.state;
    const error = (validateWhileTyping || meta.touched) && meta.error;

    let borderColor = inversedColors
      ? theme.overPrimaryColor
      : theme.textInput.underlineColor;
    if (error) {
      borderColor = theme.errorColor;
    }
    if (disabled) {
      borderColor = theme.textInput.disabled.underlineColor;
    }
    const selectionColor = Platform.select({
      ios: inversedColors
        ? theme.overPrimaryColor
        : theme.iosInputSelectionColor,
      android: inversedColors
        ? theme.overPrimaryColor
        : theme.androidInputSelectionColor,
    });

    const widthStyle = {width};

    return (
      <View style={wrapperContainerStyle}>
        <View
          style={[
            styles.container,
            inversedColors && styles.inversedColorsContainer,
            error && styles.errorContainer,
            containerStyle,
          ]}
          onLayout={this._onLayout}>
          <TextInput
            ref={this.setInputRef}
            style={[
              styles.textInput,
              inputStyle,
              widthStyle,
              inversedColors && styles.inversedColorsTextInput,
              disabled && styles.disabledTextInput,
            ]}
            editable={!disabled}
            underlineColorAndroid="transparent"
            secureTextEntry={false}
            selectionColor={selectionColor}
            placeholderTextColor={
              inversedColors
                ? theme.transparentOverPrimaryColor
                : theme.textInput.placeholderColor
            }
            {...this.getTypeProps()}
            {...input}
            {...otherProps}
            value={this.props.input.value}
          />
          {/* Commented below because of this error:
          Style property 'width' is not
          supported by native animated module [Component Stack] */}
          {/* <Animated.View
            style={[
              styles.border,
              {
                width: focusedAnim.interpolate({
                  inputRange: [0, 1],
                  outputRange: [0, width],
                }),
                backgroundColor: borderColor,
              },
            ]}
          /> */}
          {icon && (
            <View style={{position: 'absolute', right: 5, bottom: 3}}>
              {icon}
            </View>
          )}
        </View>
        <View style={styles.errorContainer}>
          {error && (
            <Text
              style={[styles.error, this.props.errorStyle]}
              errornumberoflines={errorNumberOfLines}>
              {error}
            </Text>
          )}
        </View>
      </View>
    );
  }

  focus = () => {
    if (this.props.disabled) {
      return;
    }
    this.inputRef && this.inputRef.focus();
  };

  blur = () => {
    this.inputRef && this.inputRef.blur();
  };

  isFocused = () => {
    return this.inputRef && this.inputRef.isFocused();
  };

  clear = () => {
    this.inputRef && this.inputRef.clear();
  };

  isEmpty = () => {
    return this.inputRef && this.inputRef.props && !this.inputRef.props.value;
  };
}

export type HoshiInputPropsType = {
  label?: string,
  style: *,
  wrapperContainerStyle?: *,
  inputStyle?: *,
  input: InputProps,
  meta: MetaProps,
  type?: 'text' | 'email' | 'password' | 'digits',
  icon?: any,
  easing: Function,
  animationDuration: number,
  disabled: boolean,
  type?: string,
  fontFamily?: string,
  fontWeight?: string,
  italic: boolean,
  errorStyle?: *,
  errorNumberOfLines?: number,
  inversedColors?: boolean,
  _onFocus: Function,
  validateWhileTyping: boolean,
};

type State = {
  focusedAnim: any,
  width: ?number,
};

const getStyles = () =>
  StyleSheet.create({
    container: {
      borderBottomWidth: 1,
      borderBottomColor: theme.textInput.underlineColor,
    },
    inversedColorsContainer: {
      borderBottomColor: theme.transparentPrimaryColor,
    },
    errorContainer: {
      borderBottomColor: theme.errorColor,
      minHeight: 10,
    },
    textInput: {
      height: isIos ? 42 : undefined,
      color: theme.textInput.textColor,
      fontSize: 16,
      fontFamily: theme.fontFamily,
      paddingVertical: 10,
      rtlAware: {
        textAlign: 'left',
      },
    },
    inversedColorsTextInput: {
      color: theme.overPrimaryColor,
    },
    disabledTextInput: {
      color: theme.textInput.disabled.textColor,
    },
    border: {
      position: 'absolute',
      bottom: -1,
      left: 0,
      right: 0,
      height: 2,
    },
    error: {
      textAlign: 'right',
      color: theme.errorColor,
      fontSize: 13,
      backgroundColor: 'transparent',
      bottom: 5,
    },
  });
